import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Typography } from '@material-tailwind/react';
import { PlusCircleIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

export const TabEscrituras = ({ id_obrero, esVisualizacion }) => {
    const [escrituras, setEscrituras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEscritura, setSelectedEscritura] = useState(null);

    // Dropdown states
    const [regiones, setRegiones] = useState([]);
    const [distritos, setDistritos] = useState([]);
    const [provincias, setProvincias] = useState([]);

    // Form State — todos los campos que espera el SP spWebInsActEscritura
    const initialFormState = {
        id_escritura: null,
        codigo: '',
        nro_escritura: '',
        nro_partida: '',
        tipo_propiedad: 'P',
        fecha_escritura: '',
        lugar_escritura: '',
        nombre_escribano: '',
        tipo_adquisicion: 'D',
        precio_fiscal: '',
        precio_total: '',
        direccion: '',
        cod_postal: '',
        localidad: '',
        partido: '',
        provincia: '',
        id_provincia: '',
        id_region: '',
        id_distrito: '',
        circunscripcion: '',
        seccion: '',
        manzana: '',
        lote: '',
        medidas: '',
        superficie: '',
        año_valuacion: '',
        precio: '',
        material: false,
        otros: false,
        medidas_templo: '',
        planos: 'ET',
        observaciones: ''
    };
    const [form, setForm] = useState(initialFormState);

    useEffect(() => {
        if(id_obrero) {
            buscarEscrituras(id_obrero);
        }
    }, [id_obrero]);

    // Load initial regions & provinces
    useEffect(() => {
        const cargarTablas = async () => {
            try {
                const [resRegiones, resProvincias] = await Promise.all([
                    Axios.get('/api/tablasgenerales/regiones/*'),
                    Axios.get('/api/tablasgenerales/provincias')
                ]);
                setRegiones(resRegiones.data);
                setProvincias(resProvincias.data);
            } catch (error) {
                console.error('Error al cargar regiones/provincias', error);
            }
        };
        cargarTablas();
    }, []);

    // Load districts based on selected region
    useEffect(() => {
        const cargarDistritos = async () => {
            if (form.id_region) {
                try {
                    const { data } = await Axios.get(`/api/tablasgenerales/distritos/${form.id_region}`);
                    setDistritos(data);
                } catch (error) {
                    console.error('Error al cargar distritos', error);
                    setDistritos([]);
                }
            } else {
                setDistritos([]);
            }
        };
        cargarDistritos();
    }, [form.id_region]);

    useEffect(() => {
        if (selectedEscritura && selectedEscritura.id_escritura !== form.id_escritura) {
            abrirEditar(selectedEscritura.id_escritura);
        }
    }, [selectedEscritura]);

    const buscarEscrituras = async (id) => {
        setLoading(true);
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/escrituras/${id}`);
            setEscrituras(data);
            if (data && data.length > 0 && !selectedEscritura) {
                // setSelectedEscritura dispara el useEffect que llama a abrirEditar (evitamos el doble fetch)
                setSelectedEscritura(data[0]);
            } else if (data.length === 0) {
                setForm(initialFormState);
                setSelectedEscritura(initialFormState);
            }
        } catch (err) {
            console.log('Error al buscar las escrituras del ministro', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleProvinciaChange = (e) => {
        const id = e.target.value;
        const prov = provincias.find(p => String(p.id_provincia) === String(id));
        setForm(prev => ({ ...prev, id_provincia: id, provincia: prov ? prov.nombre : '' }));
    };

    const formatearFechaParaInput = (fechaText) => {
        // Convierte DD/MM/YYYY o ISO a YYYY-MM-DD para el input type date.
        if (!fechaText) return '';
        if (fechaText.includes('-')) return fechaText.substring(0, 10);
        const partes = fechaText.split('/');
        if (partes.length === 3) {
            return `${partes[2]}-${partes[1]}-${partes[0]}`;
        }
        return fechaText;
    };

    const abrirNuevo = () => {
        setForm(initialFormState);
        setSelectedEscritura(initialFormState);
    };

    const abrirEditar = async (id) => {
        if(!id) return;
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/escritura/${id}`);
            if(data && data.length > 0) {
                const e = data[0];
                setForm({
                    id_escritura: e.id_escritura,
                    codigo: e.codigo || '',
                    nro_escritura: e.nro_escritura || '',
                    nro_partida: e.nro_partida || '',
                    tipo_propiedad: e.tipo_propiedad ? String(e.tipo_propiedad).trim() : 'P',
                    fecha_escritura: formatearFechaParaInput(e.fecha_escritura),
                    lugar_escritura: e.lugar_escritura || '',
                    nombre_escribano: e.nombre_escribano || '',
                    tipo_adquisicion: e.tipo_adquisicion ? String(e.tipo_adquisicion).trim() : 'D',
                    precio_fiscal: e.precio_fiscal || '',
                    precio_total: e.precio_total || '',
                    direccion: e.direccion || '',
                    cod_postal: e.cod_postal || '',
                    localidad: e.localidad || '',
                    partido: e.partido || '',
                    provincia: e.provincia || '',
                    id_provincia: e.id_provincia || '',
                    id_region: e.id_region || '',
                    id_distrito: e.id_distrito || '',
                    circunscripcion: e.circunscripcion || '',
                    seccion: e.seccion || '',
                    manzana: e.manzana || '',
                    lote: e.lote || '',
                    medidas: e.medidas || '',
                    superficie: e.superficie || '',
                    año_valuacion: (e.año_valuacion === null || e.año_valuacion === undefined) ? '' : e.año_valuacion,
                    precio: e.precio || '',
                    material: !!e.material,
                    otros: !!e.otros,
                    medidas_templo: e.medidas_templo || '',
                    planos: e.planos ? String(e.planos).trim() : 'ET',
                    observaciones: e.observaciones || ''
                });
            }
        } catch (err) {
            Swal.fire('Error', 'No se pudo obtener el detalle de la escritura', 'error');
            console.error(err);
        }
    };

    const borrarEscritura = (id, e) => {
        if (e) e.stopPropagation();
        if(esVisualizacion) return;
        Swal.fire({
            title: '¿Eliminar Escritura?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await Axios.delete(`/api/tablasgenerales/escritura/${id}`);
                    Swal.fire('Eliminado!', 'La escritura fue eliminada.', 'success');
                    setSelectedEscritura(null);
                    setForm(initialFormState);
                    buscarEscrituras(id_obrero);
                } catch (error) {
                    Swal.fire('Error', 'Ocurrió un error al eliminar la escritura.', 'error');
                }
            }
        });
    };

    const guardarEscritura = async () => {
        // Payload con TODOS los campos que espera el SP spWebInsActEscritura
        let payload = {
            id_escritura: form.id_escritura,
            id_obrero: id_obrero,
            codigo: form.codigo,
            nro_escritura: form.nro_escritura,
            nro_partida: form.nro_partida,
            tipo_propiedad: form.tipo_propiedad || 'P',
            fecha_escritura: form.fecha_escritura ? form.fecha_escritura : null,
            lugar_escritura: form.lugar_escritura,
            nombre_escribano: form.nombre_escribano,
            tipo_adquisicion: form.tipo_adquisicion || 'D',
            precio_fiscal: form.precio_fiscal,
            precio_total: form.precio_total,
            direccion: form.direccion,
            cod_postal: form.cod_postal,
            localidad: form.localidad,
            partido: form.partido,
            provincia: form.provincia,
            id_provincia: form.id_provincia ? Number(form.id_provincia) : 0,
            id_region: form.id_region ? Number(form.id_region) : 0,
            id_distrito: form.id_distrito ? Number(form.id_distrito) : 0,
            circunscripcion: form.circunscripcion,
            seccion: form.seccion,
            manzana: form.manzana,
            lote: form.lote,
            medidas: form.medidas,
            superficie: form.superficie,
            año_valuacion: form.año_valuacion ? Number(form.año_valuacion) : 0,
            precio: form.precio,
            material: form.material ? 1 : 0,
            otros: form.otros ? 1 : 0,
            medidas_templo: form.medidas_templo,
            planos: form.planos || 'ET',
            observaciones: form.observaciones
        };

        try {
            if (form.id_escritura) {
                await Axios.put('/api/tablasgenerales/escritura', payload);
                Swal.fire('Actualizado', 'Escritura actualizada correctamente', 'success');
            } else {
                const respuesta = await Axios.post('/api/tablasgenerales/escritura', payload);
                // El backend devuelve el id_escritura del nuevo registro en el recordset.
                // Lo fijamos para que un próximo guardado actualice (PUT) y no inserte de nuevo (POST).
                const nuevoId = respuesta?.data?.data?.[0]?.id_escritura ?? respuesta?.data?.id_escritura;
                if (nuevoId) {
                    setForm(prev => ({ ...prev, id_escritura: nuevoId }));
                    setSelectedEscritura(prev => ({ ...(prev || {}), id_escritura: nuevoId }));
                }
                Swal.fire('Guardado', 'Escritura creada correctamente', 'success');
            }
            buscarEscrituras(id_obrero);
        } catch (error) {
            console.error(error);
            const detalle = error?.response?.data?.message || error?.response?.data || error.message;
            Swal.fire('Error', `Ocurrió un error al guardar la escritura.${detalle ? ' ' + detalle : ''}`, 'error');
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        try {
            return fecha.split('T')[0].split('-').reverse().join('/');
        } catch (e) {
            return fecha;
        }
    };

    // Helper para inputs de texto (función que retorna JSX; no es un componente, así no se remonta ni pierde foco)
    const inputClass = "text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full";
    const campoTexto = (label, name, opts = {}) => (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-700">{label}</span>
            <input
                type={opts.type || 'text'}
                name={name}
                maxLength={opts.maxLength}
                placeholder={opts.placeholder}
                disabled={esVisualizacion}
                className={inputClass}
                value={form[name] ?? ''}
                onChange={handleFormChange}
            />
        </div>
    );

    return (
        <div className="bg-white p-5 rounded-md text-sm w-full">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full">
                {/* Left side: List box */}
                <div className="flex flex-col col-span-1 lg:col-span-5 border border-gray-300 bg-white shadow-sm h-full max-h-[600px] overflow-hidden">
                    <div className="bg-gray-100 border-b border-gray-300 flex justify-between items-center px-4 py-3 shrink-0">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Escrituras</span>
                    </div>
                    <div className="bg-gray-50 border-b border-gray-200 flex flex-row items-center shrink-0">
                        <div className="w-16 md:w-20 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Código</div>
                        <div className="w-20 md:w-24 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">N° Escr.</div>
                        <div className="w-20 md:w-24 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">N° Partida</div>
                        <div className="w-20 md:w-24 px-2 py-2 text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Fecha</div>
                        <div className="flex-1 px-2 py-2 text-xs font-bold text-gray-600 truncate hidden md:block">Dirección</div>
                    </div>
                    <ul className="flex-1 overflow-y-auto no-scrollbar">
                        {loading ? (
                            <div className="p-4 text-gray-400 text-xs text-center italic">Cargando...</div>
                        ) : escrituras && escrituras.length > 0 ? (
                            escrituras.map((item, idx) => (
                                <li
                                    key={idx}
                                    className={`px-2 py-3 cursor-pointer text-sm border-b border-gray-100 last:border-0 flex justify-between items-center transition-colors ${form.id_escritura === item.id_escritura ? 'bg-blue-50 text-blue-800' : 'text-gray-800 hover:bg-gray-50'}`}
                                    onClick={() => setSelectedEscritura(item)}
                                >
                                    <div className="flex flex-row w-full items-center text-center">
                                        <div className="w-16 md:w-20 truncate px-1 md:px-2 border-r border-gray-100">{item.codigo || '-'}</div>
                                        <div className="w-20 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 text-[11px] md:text-sm">{item.nro_escritura || '-'}</div>
                                        <div className="w-20 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 text-[11px] md:text-sm">{item.nro_partida || '-'}</div>
                                        <div className="w-20 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 text-[11px] md:text-sm">{item.fecha_escritura ? formatFecha(item.fecha_escritura) : '-'}</div>
                                        <div className="flex-1 truncate px-1 md:px-2 text-left text-[11px] md:text-sm hidden md:block" title={item.direccion}>{item.direccion || '-'}</div>
                                    </div>
                                    {!esVisualizacion && (
                                        <button
                                            type="button"
                                            title="Eliminar escritura"
                                            onClick={(e) => borrarEscritura(item.id_escritura, e)}
                                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors ml-2"
                                        >
                                            <TrashIcon width={18} height={18} />
                                        </button>
                                    )}
                                </li>
                            ))
                        ) : (
                            <div className="p-4 text-gray-400 text-xs text-center flex flex-col items-center justify-center h-full italic">No hay escrituras registradas</div>
                        )}
                    </ul>
                </div>

                {/* Right side: Form */}
                <div className="flex flex-col col-span-1 lg:col-span-7 bg-gray-50 border border-gray-200 rounded-md p-5 shadow-sm overflow-y-auto max-h-[600px]">
                    <Typography variant="small" color="blue-gray" className="mb-4 font-semibold uppercase tracking-wider text-xs border-b border-gray-200 pb-2">
                        {form.id_escritura ? 'Detalle de la Escritura' : 'Nueva Escritura'}
                    </Typography>

                    <div className="flex flex-col gap-5">
                        {/* Datos de la escritura */}
                        <div className="flex flex-col gap-3">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Datos de la escritura</span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {campoTexto('Código', 'codigo', { maxLength: 10 })}
                                {campoTexto('Fecha Escritura', 'fecha_escritura', { type: 'date' })}
                                {campoTexto('N° Escritura', 'nro_escritura', { maxLength: 10 })}
                                {campoTexto('N° Partida', 'nro_partida', { maxLength: 15 })}
                                {campoTexto('Lugar de escritura', 'lugar_escritura', { maxLength: 100 })}
                                {campoTexto('Escribano', 'nombre_escribano', { maxLength: 100 })}
                                {campoTexto('Tipo propiedad', 'tipo_propiedad', { maxLength: 1, placeholder: 'P' })}
                                {campoTexto('Tipo adquisición', 'tipo_adquisicion', { maxLength: 1, placeholder: 'D' })}
                            </div>
                        </div>

                        {/* Ubicación */}
                        <div className="flex flex-col gap-3">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Ubicación</span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {campoTexto('Dirección', 'direccion', { maxLength: 100 })}
                                {campoTexto('Código postal', 'cod_postal', { maxLength: 10 })}
                                {campoTexto('Localidad', 'localidad', { maxLength: 100 })}
                                {campoTexto('Partido', 'partido', { maxLength: 100 })}
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-gray-700">Provincia</span>
                                    <select name="id_provincia" disabled={esVisualizacion} className={inputClass} value={form.id_provincia} onChange={handleProvinciaChange}>
                                        <option value="">Seleccione una provincia...</option>
                                        {provincias.map(item => (
                                            <option key={item.id_provincia} value={item.id_provincia}>{item.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-gray-700">Región</span>
                                    <select
                                        name="id_region"
                                        disabled={esVisualizacion}
                                        className={inputClass}
                                        value={form.id_region}
                                        onChange={(e) => {
                                            handleFormChange(e);
                                            setForm(prev => ({...prev, id_distrito: ''}));
                                        }}
                                    >
                                        <option value="">Seleccione una región...</option>
                                        {regiones.map(item => (
                                            <option key={item.id_region} value={item.id_region}>{item.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-gray-700">Distrito</span>
                                    <select
                                        name="id_distrito"
                                        disabled={esVisualizacion || !form.id_region}
                                        className={`${inputClass} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        value={form.id_distrito}
                                        onChange={handleFormChange}
                                    >
                                        <option value="">Seleccione un distrito...</option>
                                        {distritos.map((item, idx) => (
                                            <option key={item.id_distrito_region || item.id_distrito || idx} value={item.id_distrito}>{item.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Datos catastrales */}
                        <div className="flex flex-col gap-3">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Datos catastrales</span>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {campoTexto('Circunscripción', 'circunscripcion', { maxLength: 10 })}
                                {campoTexto('Sección', 'seccion', { maxLength: 10 })}
                                {campoTexto('Manzana', 'manzana', { maxLength: 10 })}
                                {campoTexto('Lote', 'lote', { maxLength: 10 })}
                                {campoTexto('Medidas', 'medidas', { maxLength: 50 })}
                                {campoTexto('Superficie', 'superficie', { maxLength: 50 })}
                            </div>
                        </div>

                        {/* Valuación y precios */}
                        <div className="flex flex-col gap-3">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Valuación y precios</span>
                            <div className="grid grid-cols-2 gap-4">
                                {campoTexto('Año valuación', 'año_valuacion', { type: 'number' })}
                                {campoTexto('Precio', 'precio', { maxLength: 15 })}
                                {campoTexto('Precio fiscal', 'precio_fiscal', { maxLength: 15 })}
                                {campoTexto('Precio total', 'precio_total', { maxLength: 15 })}
                            </div>
                        </div>

                        {/* Templo / otros */}
                        <div className="flex flex-col gap-3">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Templo / otros</span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {campoTexto('Medidas templo', 'medidas_templo', { maxLength: 50 })}
                                {campoTexto('Planos', 'planos', { maxLength: 2, placeholder: 'ET' })}
                            </div>
                            <div className="flex flex-row gap-6 bg-white p-3 border border-gray-200 rounded-md">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" disabled={esVisualizacion} name="material" checked={!!form.material} onChange={handleFormChange} className="w-4 h-4 text-blue-600 bg-white" />
                                    <span className="text-sm text-gray-700">Material</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" disabled={esVisualizacion} name="otros" checked={!!form.otros} onChange={handleFormChange} className="w-4 h-4 text-blue-600 bg-white" />
                                    <span className="text-sm text-gray-700">Otros</span>
                                </label>
                            </div>
                        </div>

                        {/* Observaciones */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-700">Observaciones</span>
                            <textarea name="observaciones" disabled={esVisualizacion} value={form.observaciones || ''} onChange={handleFormChange} rows="3" className={inputClass}></textarea>
                        </div>

                        {!esVisualizacion && (
                            <div className="flex justify-end mt-2 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={abrirNuevo}
                                    className="text-red-400 bg-red-100 hover:bg-red-200 py-2 px-6 font-bold rounded-md shadow-sm transition-all text-sm uppercase mr-2"
                                    title="Limpiar selección / Nuevo"
                                >
                                    NUEVO
                                </button>
                                <button type="button" onClick={guardarEscritura} className="flex items-center bg-blue-500 hover:bg-blue-600 text-white py-2 px-5 rounded-md text-sm font-bold transition-colors shadow-sm"><CheckIcon className="w-5 h-5 mr-1" strokeWidth={2} /> GUARDAR</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
